import { DeleteOutlined, EditOutlined, PlusOutlined, SaveOutlined, ExclamationCircleOutlined } from "@ant-design/icons";
import { Col, Input, InputNumber, Modal, Row, Select, Spin, Table } from "antd";
import { useFormik } from "formik";
import { get } from "lodash";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import Button from "../../components/functional_components/Button/PFButton";
import { setLoading } from "../../redux/ducks/Loading";
import FlatRateLocation from "../../services/api/FlatRateLocation";
import { flatRateTypes, paginationDefaults } from "../../utils/properties";
import { buildQueryString, showError, showSuccess, trimData } from "../../utils/utilities";
import Filter from "../../components/functional_components/Filter/Filter";
import { setFilters } from "../../redux/ducks/Filters";

const { Option } = Select;

const FlatRateLocations = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { confirm } = Modal;
  const filters = useSelector((state) => state.FiltersDuck.filters);
  const [state, setState] = useState({
    tableContent: [],
    pagination: {
      currentPage: paginationDefaults.page,
      size: paginationDefaults.size,
      totalItems: paginationDefaults.totalItems,
    },
    modal: {
      visible: false,
      type: "",
      data: {},
    },
    loading: false,
  });

  useEffect(() => {
    const queryString = buildQueryString(filters.obj, {});
    dispatch(setFilters(queryString));
    dataList(queryString.string);
  }, []);

  const formikFlatRateLocation = useFormik({
    initialValues: {
      hours: get(state.modal.data, "hours", ""),
      location: get(state.modal.data, "location", ""),
      type: get(state.modal.data, "type", ""),
    },
    validate: (values) => {
      values = trimData(values);
      let errors = {};
      if (!values.hours) {
        errors.hours = "Required!";
      }
      if (!values.location) {
        errors.location = "Required!";
      }
      if (!values.type) {
        errors.type = "Required!";
      }
      return errors;
    },
    enableReinitialize: true,
    onSubmit: async (values) => {
      setState((prevState) => ({ ...prevState, loading: true }));
      const data = {
        hours: values.hours,
        location: values.location,
        type: values.type,
        id: state.modal.data?.id || undefined,
      };
      const saveResponse = await FlatRateLocation.setFlatRateLocation(data);
      if (!saveResponse.error) {
        showSuccess();
        closeModal();
        const queryString = buildQueryString(filters.obj, {});
        dispatch(setFilters(queryString));
        dataList(queryString.string);
      } else {
        showError(get(saveResponse, "error.messageCode", "ER000"));
      }
      setState((prevState) => ({ ...prevState, loading: false }));
    },
  });

  const dataList = async (queryString) => {
    dispatch(setLoading(true));
    const { data, error, currentPage, pageSize, totalItems } = await FlatRateLocation.getFlatRateLocations(queryString);
    if (!error && data) {
      data.forEach((cc, i) => {
        cc.key = i; // This is necessary to remove warning in console for unique key row
      });
      setState((prevState) => ({
        ...prevState,
        tableContent: data || [],
        pagination: {
          currentPage: currentPage ? currentPage + 1 : paginationDefaults.page,
          size: pageSize || paginationDefaults.size,
          totalItems: totalItems || paginationDefaults.totalItems,
        },
      }));
    } else {
      showError(get(error, "messageCode", "ER000"));
    }
    dispatch(setLoading(false));
  };

  const closeModal = () => {
    formikFlatRateLocation.resetForm();
    setState((prevState) => ({ ...prevState, modal: { visible: false, type: "", data: {} } }));
  };

  const editCallback = async (id) => {
    setState((prevState) => ({ ...prevState, loading: true, modal: { visible: true, type: "edit" } }));
    const { data, error } = await FlatRateLocation.getFlatRateLocation(id);
    if (!error && data) {
      setState((prevState) => ({ ...prevState, modal: { visible: true, type: "edit", data } }));
    } else {
      showError(get(error, "messageCode", "ER000"));
    }
    setState((prevState) => ({ ...prevState, loading: false }));
  };

  const deleteCallback = (id) => {
    confirm({
      title: t("general.ensureDelete"),
      icon: <ExclamationCircleOutlined />,
      onOk: async () => {
        const deleteResponse = await FlatRateLocation.deleteFlatRateLocation(id);
        if (!deleteResponse.error) {
          showSuccess();
          const queryString = buildQueryString(filters.obj, {});
          dispatch(setFilters(queryString));
          dataList(queryString.string);
        } else {
          showError(get(deleteResponse, "error.messageCode", "ER000"));
        }
      },
      onCancel() {},
    });
  };

  const columns = [
    {
      title: t("flatRateLocation.table.hours"),
      dataIndex: "hours",
      key: "hours",
    },
    {
      title: t("flatRateLocation.table.location"),
      dataIndex: "location",
      key: "location",
    },
    {
      title: t("flatRateLocation.table.type"),
      dataIndex: "type",
      key: "type",
      render: (type) => t(`flatRateTypes.${type}`),
    },
    {
      title: t("general.actions"),
      key: "action",
      width: 80,
      render: (data) => {
        return (
          <div className="space-around">
            <div onClick={() => editCallback(data.id)}>
              <EditOutlined className="icon-medium" />
            </div>
            <div onClick={() => deleteCallback(data.id)}>
              <DeleteOutlined className="icon-medium" />
            </div>
          </div>
        );
      },
    },
  ];

  const formikFilter = useFormik({
    initialValues: {
      hours: "",
      location: "",
      type: "",
    },
    enableReinitialize: true,
    onSubmit: (values) => {
      // When I search, I start my page to 0
      values["page"] = 0;
      const queryString = buildQueryString(filters.obj, values);
      dispatch(setFilters(queryString));
      dataList(queryString.string);
    },
  });

  const fieldsFilter = [
    {
      type: "input",
      isRequired: false,
      id: "hours",
      name: "hours",
      value: formikFilter.values.hours,
      label: t("flatRateLocation.table.hours"),
      onChange: formikFilter.handleChange,
      onBlur: formikFilter.handleBlur,
    },
    {
      type: "input",
      isRequired: false,
      id: "location",
      name: "location",
      value: formikFilter.values.location,
      label: t("flatRateLocation.table.location"),
      onChange: formikFilter.handleChange,
      onBlur: formikFilter.handleBlur,
    },
    {
      type: "select",
      isRequired: false,
      id: "type",
      name: "type",
      value: formikFilter.values.type,
      label: t("flatRateLocation.table.type"),
      onChange: formikFilter.handleChange("type"),
      onBlur: formikFilter.handleBlur("type"),
      dataSelect: flatRateTypes.map((c) => ({ value: c, label: t(`flatRateTypes.${c}`) })),
    },
  ];

  const resetFilter = () => {
    const queryString = buildQueryString({ page: paginationDefaults.page - 1, size: paginationDefaults.size }, {});
    dispatch(setFilters(queryString));
    dataList(queryString.string);
  };

  return (
    <>
      <Filter formikForm={formikFilter} fields={fieldsFilter} onReset={resetFilter} allInOneRow />
      <Row type="flex" align="middle" justify="end" className="mb-10">
        <Button
          currentIcon={<PlusOutlined className="icon-medium" />}
          clickCallback={() => setState((prevState) => ({ ...prevState, modal: { visible: true, type: "new" } }))}
        />
      </Row>
      <Table
        dataSource={state.tableContent}
        columns={columns}
        size="small"
        pagination={{
          current: state.pagination.currentPage,
          pageSize: state.pagination.size,
          total: state.pagination.totalItems,
          onChange: (page, pageSize) => {
            const queryString = buildQueryString(filters.obj, { page: page - 1, size: pageSize });
            dispatch(setFilters(queryString));
            setState((prevState) => ({
              ...prevState,
              pagination: {
                currentPage: page,
                pageSize: pageSize,
                totalItems: state.pagination.totalItems,
              },
            }));
            dataList(queryString.string);
          },
        }}
      />
      <Modal
        open={state.modal.visible}
        width={700}
        title={
          state.modal.type === "edit" && state.modal.data && state.modal.data.brand
            ? state.modal.data.brand
            : t("flatRateLocation.new")
        }
        footer={[
          <Button
            key="okButton"
            currentIcon={<SaveOutlined className="icon-medium" />}
            clickCallback={formikFlatRateLocation.submitForm}
            isDisabled={!(formikFlatRateLocation.dirty && formikFlatRateLocation.isValid)}
          />,
        ]}
        onCancel={closeModal}
      >
        {state.loading ? (
          <div className="container-spinner-positioning">
            <Spin spinning={state.loading} className="spinner-positioning" />
          </div>
        ) : (
          ""
        )}
        <Row gutter={[40, 24]}>
          <Col lg={8} md={8} xs={24} sm={8}>
            <Row>
              <label className="fw-bold mt-10" htmlFor="hours">
                {t("flatRateLocation.table.hours")}*
              </label>
            </Row>
            <Row>
              <InputNumber
                className="ant-input"
                controls={false}
                required
                id="hours"
                name="hours"
                onChange={(e) => formikFlatRateLocation.setFieldValue("hours", e)}
                onBlur={formikFlatRateLocation.handleBlur}
                value={formikFlatRateLocation.values.hours}
                precision={2}
                decimalSeparator=","
              />
            </Row>
          </Col>
          <Col lg={8} md={8} xs={24} sm={8}>
            <Row>
              <label className="fw-bold mt-10" htmlFor="location">
                {t("flatRateLocation.table.location")}*
              </label>
            </Row>
            <Row>
              <Input
                required
                id="location"
                name="location"
                onChange={formikFlatRateLocation.handleChange}
                onBlur={formikFlatRateLocation.handleBlur}
                value={formikFlatRateLocation.values.location}
              />
            </Row>
          </Col>
          <Col lg={8} md={8} xs={24} sm={8}>
            <Row>
              <label className="fw-bold mt-10" htmlFor="type">
                {t("flatRateLocation.table.type")}*
              </label>
            </Row>
            <Row>
              <Select
                allowClear
                className="w-100"
                id="type"
                name="type"
                onChange={(value) => formikFlatRateLocation.setFieldValue("type", value)}
                placeholder={t("flatRateLocation.table.type")}
                onBlur={formikFlatRateLocation.handleBlur("type")}
                value={formikFlatRateLocation.values.type}
              >
                {flatRateTypes.map((data, idx) => (
                  <Option key={idx} value={data}>
                    {t(`flatRateTypes.${data}`)}
                  </Option>
                ))}
              </Select>
            </Row>
          </Col>
        </Row>
      </Modal>
    </>
  );
};

export default FlatRateLocations;
